import {
   HttpErrorResponse,
   HttpEvent,
   HttpHandler,
   HttpInterceptor,
   HttpRequest,
   HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
   BatchGraphErrors,
   ExceptionType,
   GraphError,
   RestApiError,
   SingleGraphError,
} from '../common-components/errors/models/error-message';

@Injectable()
// Interceptor parses errors out of response body for graph requests.
export class GraphQLErrorHttpInterceptor implements HttpInterceptor {
   public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      const next$ = next.handle(req);
      if (req.url.includes('/api/graphql')) {
         return this.handleGraphqlResponse(next$);
      } else {
         return this.handleRestResponse(next$);
      }
   }

   private handleGraphqlResponse(next: Observable<HttpEvent<any>>) {
      return next.pipe(
         switchMap((response) => {
            let error: BatchGraphErrors | SingleGraphError | null = null;
            if (response instanceof HttpResponse) {
               if (response.body) {
                  if (Array.isArray(response.body)) {
                     error = this.tryBuildBatchError(response);
                  } else {
                     error = this.tryBuildSingleError(response);
                  }
               }
            }

            if (error) {
               return throwError(error);
            }

            // Pass response through if no graphql errors
            return of(response);
         })
      );
   }

   private handleRestResponse(next: Observable<HttpEvent<any>>) {
      return next.pipe(
         catchError((rawError: HttpErrorResponse) => {
            let error: RestApiError | null = null;
            if (rawError) {
               error = { ...rawError.error, exceptionType: ExceptionType.RestApi, rawError };
            }

            return throwError(error);
         })
      );
   }

   private tryBuildBatchError(response: HttpResponse<GraphError[]>): BatchGraphErrors | null {
      const body = response.body;
      if (body) {
         const errors: GraphError[] = body.filter((b) => this.checkBodyPartForErrors(b));
         if (errors && errors.length > 0) {
            return {
               error: body,
               exceptionType: ExceptionType.BatchGraph,
            };
         }
      }

      return null;
   }

   private tryBuildSingleError(response: HttpResponse<GraphError>): SingleGraphError | null {
      const body = response.body;
      if (body && this.checkBodyPartForErrors(body)) {
         return { error: body, exceptionType: ExceptionType.Graph };
      }

      return null;
   }

   private checkBodyPartForErrors(bodyPart: any) {
      return bodyPart.errors && bodyPart.errors.length > 0;
   }
}
